import { RRCProps, ReduxReusableComponents } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { GanttUtils } from "../gantt/GanttUtils";
import { AbstractGantt, AbstractGanttProps, AbstractGanttReducers, AbstractGanttState, ContextProps, GanttData } from "../gantt/AbstractGantt";
import { HighlightedInterval, Marker } from "@crispico/react-timeline-10000";
import _ from "lodash";
import { Segment } from "semantic-ui-react";
import { ColumnDefinition, EntityDescriptor } from "@crispico/foundation-react";
import { taskEntityDescriptor } from "AppEntityDescriptors";
import moment from "moment";
import { PARENT_ELEMENT } from "@crispico/react-timeline-10000/types/timeline";
import { MissionItemRenderer } from "pages/gantt/typeRenderers/MissionItemRenderer";

const SNAP = 10;

class MissionGanttComponentState extends AbstractGanttState {
    tableWidth: number = 300;
}

class MissionGanttComponentReducers<S extends MissionGanttComponentState = MissionGanttComponentState> extends AbstractGanttReducers<S> { }

// lot of props from abstractGantt isn't used(e.g. entitiesVersion, hideResources), but we need to provide it because the component extend the AbstractGantt
export type MissionGanttComponentProps = {
    dateOffset?: number;
    displayItemOnSeparateRowIfOverlap?: boolean;
    showPlannedTime?: boolean;
    markerDates?: number[];
} & AbstractGanttProps;

export class MissionGanttComponent extends AbstractGantt<MissionGanttComponentProps, MissionGanttComponentReducers, MissionGanttComponentState> {

    static defaultProps = {
        // default 10min
        dateOffset: 600000,
        displayItemOnSeparateRowIfOverlap: false,
        showPlannedTime: false,
        markerDates: []
    }

    get missionId() {
        if (!this.props.entities) {
            return 0;
        }
        const keys = Object.keys(this.props.entities["Mission2"]);
        return keys.length ? Number(keys[0]) : 0;
    }

    async componentDidMount() {
        await this.updateStartEndDates();
    }

    protected async componentDidUpdateInternal(prevProps?: RRCProps<MissionGanttComponentState, MissionGanttComponentReducers> & MissionGanttComponentProps) {
        if (prevProps && (prevProps.dateOffset != this.props.dateOffset || this.missionId != this.missionId || !_.isEqual(prevProps.entities, this.props.entities))) {
            this.updateStartEndDates();
        }
        if (prevProps && (prevProps.showPlannedTime != this.props.showPlannedTime || !_.isEqual(prevProps.entities, this.props.entities))) {
            this.processData(this.props.entities);
        }
    }

    protected async updateStartEndDates() {
        const mission = GanttUtils.findByUid(GanttUtils.toEntityUid("Mission2", this.missionId), this.props.entities);
        const start = new Date(mission.startTime || this.props.s.start).getTime() - this.props.dateOffset!;
        const end = new Date(mission.endTime || this.props.s.end).getTime() + this.props.dateOffset!;
        await this.props.r.setInReduxState({ start, end });
        this.processData(this.props.entities);
    }

    protected processData(newEntities: any) {
        let mission: any;
        if (!newEntities || Object.keys(newEntities).length == 0 || !(mission = GanttUtils.findByUid(GanttUtils.toEntityUid("Mission2", this.missionId), newEntities))) {
            return;
        }

        const data: GanttData = { layers: [], items: [], groups: [] };
        let groupIndex = -1;
        mission.tasks.forEach((task: any) => {
            groupIndex++;
            data.groups.push({
                key: GanttUtils.toEntityUid("Task", task.id),
                entityUid: GanttUtils.toEntityUid("Task", task.id),
                id: groupIndex,
                visible: true
            });
            const start = new Date(task.actualStartTime).getTime();
            const end = new Date(task.actualEndTime).getTime();

            data.items.push({
                key: GanttUtils.toEntityUid("Task", task.id),
                entityUid: GanttUtils.toEntityUid("Task", task.id),
                row: groupIndex,
                start,
                end,
                ganttId: this.constructor.name,
                visible: true
            });
            this.props.showPlannedTime && data.layers.push({
                rowNumber: groupIndex,
                start,
                end,
                style: { backgroundColor: "red", opacity: '0.2' },
                visible: true
            });
            mission.events.filter((e: any) => new Date(e.creationdate).getTime() >= start && new Date(e.creationdate).getTime() <= end)
                .sort((x: any, y: any) => {
                    return new Date(x.creationdate).getTime() - new Date(y.creationdate).getTime();
                }).forEach((event: any) => {
                    data.items.push({
                        key: GanttUtils.toEntityUid("MissionEvent2", event.id),
                        entityUid: GanttUtils.toEntityUid("MissionEvent2", event.id),
                        row: groupIndex,
                        start: new Date(event.creationdate).getTime(),
                        end: this.getEventItemEnd(new Date(event.creationdate).getTime()),
                        ganttId: this.constructor.name,
                        visible: true
                    })
                })
        })

        this.props.r.setInReduxState({ data });
    }

    private getEventItemEnd(start: number) {
        const x = this.getPixelAtTime(moment(start)) + PARENT_ELEMENT(this.timelineId).getBoundingClientRect().left;
        return this.getTimeAtPixel(x + 10).valueOf();
    }

    protected getSnap(): number | undefined {
        return SNAP;
    }

    protected getTableColumns(): ColumnDefinition[] | undefined {
        const columns = super.getTableColumns();
        if (columns) {
            return columns;
        }
        const fields = ["name", "startTime", "endTime"];
        let cc: ColumnDefinition[] = [];
        fields.forEach(f => cc.push({ width: 100, name: f }));
        return cc;
    }

    protected getEntityDescriptor(): EntityDescriptor {
        return taskEntityDescriptor;
    }

    protected getContextProps(): ContextProps {
        return {
            ... super.getContextProps(),
            useDnD: false
        };
    }

    protected getHighlightedIntervals(): JSX.Element[] {
        const mission = GanttUtils.findByUid(GanttUtils.toEntityUid("Mission2", this.missionId), this.props.entities);
        if (!mission) {
            return [];
        }
        const highlightedIntervals = [];
        highlightedIntervals.push(<HighlightedInterval start={new Date(mission.startTime).getTime()} end={new Date(mission.endTime).getTime()}
            style={{ backgroundColor: MissionItemRenderer.getMissionColor(mission, 0.2) }} />);

        if (this.props.showPlannedTime && mission.initialStartTime && mission.initialEndTime) {
            highlightedIntervals.push(<HighlightedInterval
                start={new Date(mission.initialStartTime).getTime()}
                end={new Date(mission.initialEndTime).getTime()}
                style={{ backgroundColor: "red", opacity: '0.3' }} />
            );
        }
        return highlightedIntervals;
    }

    protected getMarkers(): JSX.Element[] {
        return this.props.markerDates?.map(date => <Marker style={{ backgroundColor: 'red' }} date={date} />) || [];
    }

    protected getTimelineProps() {
        return {
            ...super.getTimelineProps(),
            droppableProps: { draggable: false },
            displayItemOnSeparateRowIfOverlap: this.props.displayItemOnSeparateRowIfOverlap,
            onItemHover: this.onItemHover,
            onSplitChange: (tableWidth: number) => {
                this.processData(this.props.entities);
                this.props.r.setInReduxState({ tableWidth });
            },
        };
    }

    render() {
        return <Segment className="flex-container flex-grow gap10" style={{ minHeight: '300px' }}>
            {super.render()}
        </Segment>
    }
}

export const MissionGanttComponentRRC = ReduxReusableComponents.connectRRC(MissionGanttComponentState, MissionGanttComponentReducers, MissionGanttComponent);
